import { useEffect, useState } from "react"
import { toast } from "sonner"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { FrameFormatIcon, FrameFormatSelect } from "@/components/ui/frame-format-select"
import { Button } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { patchShotFrameFormat } from "@/lib/inertia-actions"
import {
  effectiveFrameFormat,
  frameFormatLabel,
  frameFormatShort,
  isExplicitShotFrameOverride,
  normalizeFrameFormat,
  type FrameFormat,
} from "@/lib/frame-format"

export type ShotFrameFormatChipProps = {
  projectId: number
  shotId: number
  /** Shot override; null/undefined inherits project. */
  shotFrameFormat?: string | null
  projectFrameFormat: string
  /** Optimistic override after save click; null means inherit project. */
  onSaved?: (override: FrameFormat | null) => void
  /** After successful PATCH — e.g. reload storyboard tree badges. */
  onPersisted?: () => void
}

export function ShotFrameFormatChip({
  projectId,
  shotId,
  shotFrameFormat,
  projectFrameFormat,
  onSaved,
  onPersisted,
}: ShotFrameFormatChipProps) {
  const { t } = useTranslation(["shot", "common"])
  const { project_permissions } = useShotPanelWorkspace()
  const canEdit = Boolean(project_permissions?.can_edit_content)
  const projectNorm = normalizeFrameFormat(projectFrameFormat)
  const hasOverride = isExplicitShotFrameOverride(shotFrameFormat)
  const current = effectiveFrameFormat(shotFrameFormat, projectFrameFormat)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<FrameFormat>(current)

  const short = frameFormatShort(current)
  const formatLabel = frameFormatLabel(current)

  useEffect(() => {
    if (open) setDraft(effectiveFrameFormat(shotFrameFormat, projectFrameFormat))
  }, [open, shotFrameFormat, projectFrameFormat])

  // Choosing the project ratio clears override (inherit). Same effective look, but no sticky column.
  const clearsOverride = draft === projectNorm
  const noChange = clearsOverride ? !hasOverride : shotFrameFormat === draft

  const handleSave = () => {
    if (noChange) {
      setOpen(false)
      return
    }
    const nextOverride: FrameFormat | null = clearsOverride ? null : draft
    const prevOverride: FrameFormat | null = hasOverride ? normalizeFrameFormat(shotFrameFormat) : null
    onSaved?.(nextOverride)
    setOpen(false)
    void patchShotFrameFormat(projectId, shotId, nextOverride)
      .then(() => {
        onPersisted?.()
      })
      .catch((err: Error) => {
        onSaved?.(prevOverride)
        toast.error(err.message || t("common.saveErrorRetry"))
      })
  }

  const chip = (
    <>
      <FrameFormatIcon format={current} className="h-3.5 w-3.5 shrink-0" />
      <span className="tabular-nums">{short}</span>
    </>
  )

  return (
    <>
      {canEdit ? (
        <ActionHintTooltip label={t("panel.changeFrameFormat")}>
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md text-xs font-normal text-ui-text-secondary hover:text-ui-text-primary focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
            aria-label={formatLabel}
            onClick={() => setOpen(true)}
          >
            {chip}
          </button>
        </ActionHintTooltip>
      ) : (
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs font-normal text-ui-text-secondary"
          aria-label={formatLabel}
          title={formatLabel}
        >
          {chip}
        </span>
      )}

      {canEdit ? (
        <WorkspaceDialog
          open={open}
          onOpenChange={setOpen}
          title={t("panel.frameFormat")}
          description={t("panel.frameFormatHint")}
          closeAriaLabel={t("common:close")}
          size="sm"
          nested
          bodyClassName="p-5 pt-4"
          footer={
            <>
              <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="button" size="sm" onClick={handleSave}>
                {t("common:save")}
              </Button>
            </>
          }
        >
          <FrameFormatSelect value={draft} onChange={setDraft} />
        </WorkspaceDialog>
      ) : null}
    </>
  )
}
